import { BaseService } from './BaseService';
import { apiService } from './apiService';
import {
  AdminSessionsResponse,
  AdminSessionMessagesResponse,
  AdminSessionFeedbacksResponse,
  AdminAllFeedbacksResponse,
  AdminSessionsFilter,
  AdminSessionMessagesFilter
} from '../types/admin';
import { ADMIN_SERVICE, PAGINATION } from '../config/defaults';

/**
 * Service for Admin API operations
 * Handles sessions-all and feedbacks endpoints with proper authorization
 */
export class AdminService extends BaseService {
  constructor() {
    super('AdminService', {
      debug: false, // Disable verbose logging for admin operations
      timeout: ADMIN_SERVICE.TIMEOUT,
      maxRetryAttempts: ADMIN_SERVICE.MAX_RETRY_ATTEMPTS
    });
  }

  // Required abstract method implementations
  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retry attempt ${attempt} for ${operationName} after ${delay}ms`, { error: error.message });
  }

  /**
   * Get all sessions for all users with pagination support
   */
  async getAllSessions(filter: AdminSessionsFilter = {}): Promise<AdminSessionsResponse> {
    return this.executeOperation(async () => {
      const params = new URLSearchParams();
      
      if (filter.limit !== undefined) {
        params.append('limit', Math.min(filter.limit, PAGINATION.ADMIN_MAX_LIMIT).toString());
      } else {
        params.append('limit', PAGINATION.CHAT_SESSIONS_PAGE_SIZE.toString()); // Default to page size
      }
      
      if (filter.continuationToken) {
        params.append('continuationToken', filter.continuationToken);
      }
      
      if (filter.offset !== undefined) {
        params.append('offset', filter.offset.toString());
      }
      
      if (filter.sessionType) {
        params.append('sessionType', filter.sessionType);
      }

      const url = `/api/management/sessions-all${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await apiService.get<AdminSessionsResponse>(url);
      
      if (!response || !response.sessions) {
        throw new Error('Invalid response format');
      }

      // No client-side filtering - all filtering is now done locally in useAdminSessions hook
      return response;
    }, 'getAllSessions', { metadata: filter });
  }

  /**
   * Get messages for a specific session with pagination support
   * Uses the new /api/management/session-messages endpoint
   * Only works with Foundry sessions (not traditional chat sessions)
   */
  async getSessionMessages(sessionId: string, filter: AdminSessionMessagesFilter = {}): Promise<AdminSessionMessagesResponse> {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    return this.executeOperation(async () => {
      const params = new URLSearchParams();
      
      // Set default limit to 50 as per the new endpoint specification
      if (filter.limit !== undefined) {
        params.append('limit', Math.min(filter.limit, 100).toString());
      } else {
        params.append('limit', '50');
      }
      
      if (filter.order) {
        params.append('order', filter.order);
      } else {
        params.append('order', 'asc'); // Default to oldest first
      }
      
      if (filter.after) {
        params.append('after', filter.after);
      }
      
      if (filter.before) {
        params.append('before', filter.before);
      }
      
      if (filter.all) {
        params.append('all', 'true');
      }
      
      const queryString = params.toString();
      const url = `/api/management/session-messages/${encodeURIComponent(sessionId)}${queryString ? '?' + queryString : ''}`;
      const response = await apiService.get<AdminSessionMessagesResponse>(url);
      
      if (!response || !response.sessionInfo) {
        throw new Error('Invalid response format');
      }

      return response;
    }, 'getSessionMessages', { metadata: { sessionId, filter } });
  }

  /**
   * Get feedbacks for a specific session
   */
  async getSessionFeedbacks(sessionId: string, isLatest: boolean = true): Promise<AdminSessionFeedbacksResponse> {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    return this.executeOperation(async () => {
      const params = new URLSearchParams();
      if (isLatest) {
        params.append('isLatest', 'true');
      }

      const url = `/api/management/session-feedbacks/${encodeURIComponent(sessionId)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiService.get<AdminSessionFeedbacksResponse>(url);
      
      if (!response || !response.feedbacks) {
        throw new Error('Invalid response format');
      }

      return response;
    }, 'getSessionFeedbacks', { metadata: { sessionId, isLatest } });
  }

  /**
   * Get all feedbacks from all sessions with pagination
   */
  async getAllFeedbacks(limit: number = PAGINATION.ADMIN_FEEDBACKS_DEFAULT_LIMIT, continuationToken?: string, isLatest: boolean = true): Promise<AdminAllFeedbacksResponse> {
    return this.executeOperation(async () => {
      const params = new URLSearchParams();
      params.append('limit', Math.min(limit, PAGINATION.ADMIN_MAX_LIMIT).toString());
      
      if (continuationToken) {
        params.append('continuationToken', continuationToken);
      }
      
      if (isLatest) {
        params.append('isLatest', 'true');
      }

      const url = `/api/management/feedbacks-all?${params.toString()}`;
      const response = await apiService.get<AdminAllFeedbacksResponse>(url);
      
      if (!response || !response.feedbacks) {
        throw new Error('Invalid response format');
      }

      return response;
    }, 'getAllFeedbacks', { metadata: { limit, isLatest } });
  }
}

// Export singleton instance
export const adminService = new AdminService();